import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import userService from '../services/userService';
import sponsorService from '../services/sponsorService';
import studyService from '../services/studyService';
import siteService from '../services/siteService';
import { PlusCircle, User, Users, Search, RefreshCw, MapPin } from 'lucide-react';
import AddUserModal from '../components/User/AddUserModal';
import toast from 'react-hot-toast';

// Define role hierarchy for permissions
const ROLE_HIERARCHY = {
  'program_admin': 1, // Highest level
  'project_admin': 2,
  'data_manager': 3,
  'study_team': 3,
  'study_coordinator': 4,
  'investigator': 5,
  'cra': 5,
};

// Roles that can add users
const CAN_ADD_USERS = ['program_admin', 'project_admin', 'study_team'];

const UserManagement = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [studies, setStudies] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTab, setSelectedTab] = useState('users');
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [sponsorId, setSponsorId] = useState(null);
  
  // Log user information for debugging
  useEffect(() => {
    console.log('UserManagement: Current user context:', user);
    
    // Try to determine the user's sponsor ID from various possible structures
    let sponsorId = null;
    
    if (user?.sponsor_id) {
      sponsorId = user.sponsor_id;
      console.log('UserManagement: Found sponsor_id directly:', sponsorId);
    } else if (user?.sponsor?.id) {
      sponsorId = user.sponsor.id;
      console.log('UserManagement: Found sponsor via sponsor.id:', sponsorId);
    } else if (user?.sponsors && user.sponsors.length > 0) {
      sponsorId = user.sponsors[0].id;
      console.log('UserManagement: Found sponsor via sponsors array:', sponsorId);
    } else {
      console.warn('UserManagement: No sponsor found for current user!');
    }
    
    // Store the sponsor ID in component state for use in the modal
    setSponsorId(sponsorId);
  }, [user]);
  
  // Check if current user can add users
  const canAddUsers = user && CAN_ADD_USERS.includes(user.role);
  
  // Calculate available roles based on current user's role
  useEffect(() => {
    if (user && user.role) {
      const currentUserLevel = ROLE_HIERARCHY[user.role] || 999;
      const roles = userService.getRoles().filter(role => {
        // Only show roles that are lower in hierarchy than current user
        const roleLevel = ROLE_HIERARCHY[role.value] || 999;
        return roleLevel > currentUserLevel;
      });
      setAvailableRoles(roles);
    }
  }, [user]);
  
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getUsers();
      setUsers(response);
    } catch (error) {
      toast.error('Failed to load users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSponsors = async () => {
    try {
      const response = await sponsorService.getSponsors();
      setSponsors(response);
    } catch (error) {
      toast.error('Failed to load sponsors');
      console.error('Error fetching sponsors:', error);
    }
  };
  
  const fetchStudies = async () => {
    try {
      const response = await studyService.getStudies();
      setStudies(response);
    } catch (error) {
      toast.error('Failed to load studies');
      console.error('Error fetching studies:', error);
    }
  };
  
  const fetchSites = async () => {
    try {
      const response = await siteService.getAllSites();
      setSites(response.sites || response || []);
    } catch (error) {
      toast.error('Failed to load sites');
      console.error('Error fetching sites:', error);
    }
  };
  
  useEffect(() => {
    fetchUsers();
    fetchSponsors();
    fetchStudies();
    fetchSites();
  }, []);
  
  const handleAddUser = () => {
    setIsAddUserModalOpen(true);
  };
  
  const handleUserAdded = () => {
    fetchUsers();
    toast.success('User added successfully!');
  };
  
  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.email?.toLowerCase().includes(query) ||
      user.first_name?.toLowerCase().includes(query) ||
      user.last_name?.toLowerCase().includes(query) ||
      user.role?.toLowerCase().includes(query)
    );
  });
  
  const handleAssignSponsor = async (userId, sponsorId) => {
    try {
      await userService.assignSponsor(userId, sponsorId);
      fetchUsers(); // Refresh user list
      toast.success('Sponsor assigned successfully');
    } catch (error) {
      toast.error('Failed to assign sponsor');
      console.error('Error assigning sponsor:', error);
    }
  };
  
  const handleAssignStudy = async (userId, studyId) => {
    try {
      await userService.assignStudy(userId, studyId);
      fetchUsers(); // Refresh user list
      toast.success('Study assigned successfully');
    } catch (error) {
      toast.error('Failed to assign study');
      console.error('Error assigning study:', error);
    }
  };
  
  const handleAssignSite = async (userId, siteId) => {
    try {
      await userService.assignSite(userId, siteId);
      fetchUsers(); // Refresh user list
      toast.success('Site assigned successfully');
    } catch (error) {
      toast.error('Failed to assign site');
      console.error('Error assigning site:', error);
    }
  };
  
  // Helper function to get role label
  const getRoleLabel = (roleValue) => {
    const role = userService.getRoles().find(r => r.value === roleValue);
    return role ? role.label : roleValue;
  };
  
  return (
    <div className="container mx-auto px-4 py-6">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center">
          <Users className="h-8 w-8 text-primary-600 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
            <p className="text-gray-600 mt-1">Manage users, assign them to sponsors and studies</p>
          </div>
        </div>
      </div>
      
      <div className="mt-6">
        <div className="flex justify-between items-center mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all w-64"
            />
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={() => fetchUsers()}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Refresh</span>
            </button>
            
            {canAddUsers && (
              <button
                onClick={handleAddUser}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
              >
                <PlusCircle className="h-5 w-5" />
                <span>Add User</span>
              </button>
            )}
          </div>
        </div>
        
        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="users" className="flex-1">User List</TabsTrigger>
            <TabsTrigger value="sponsors" className="flex-1">Sponsor Assignments</TabsTrigger>
            <TabsTrigger value="studies" className="flex-1">Study Assignments</TabsTrigger>
            <TabsTrigger value="sites" className="flex-1">Site Assignments</TabsTrigger>
          </TabsList>
          
          <TabsContent value="users">
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                        Loading users...
                      </td>
                    </tr>
                  ) : filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((userItem) => (
                      <tr key={userItem.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                              <User className="h-5 w-5 text-primary-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{userItem.first_name} {userItem.last_name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{userItem.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{getRoleLabel(userItem.role)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${userItem.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {userItem.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button
                            onClick={() => setSelectedUser(userItem)}
                            className="text-primary-600 hover:text-primary-900 mr-3"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
          
          <TabsContent value="sponsors">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium mb-4">Sponsor Assignments</h3>
              <p className="text-gray-500">
                This feature allows you to assign users to specific sponsors, giving them the appropriate access rights.
              </p>
              
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-3">Select User</h4>
                  <select 
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 appearance-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    value={selectedUser?.id || ""}
                    onChange={(e) => {
                      const userId = e.target.value;
                      const user = users.find(u => u.id === userId);
                      setSelectedUser(user);
                    }}
                  >
                    <option value="">Select a user</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-3">Assign to Sponsor</h4>
                  <select 
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 appearance-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    onChange={(e) => {
                      if (selectedUser) {
                        handleAssignSponsor(selectedUser.id, e.target.value);
                      }
                    }}
                  >
                    <option value="">Select a sponsor</option>
                    {sponsors.map(sponsor => (
                      <option key={sponsor.id} value={sponsor.id}>
                        {sponsor.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="studies">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium mb-4">Study Assignments</h3>
              <p className="text-gray-500">
                This feature allows you to assign users to specific studies, determining which data they can access.
              </p>
              
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-3">Select User</h4>
                  <select 
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 appearance-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    value={selectedUser?.id || ""}
                    onChange={(e) => {
                      const userId = e.target.value;
                      const user = users.find(u => u.id === userId);
                      setSelectedUser(user);
                    }}
                  >
                    <option value="">Select a user</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-3">Assign to Study</h4>
                  <select 
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 appearance-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    onChange={(e) => {
                      if (selectedUser) {
                        handleAssignStudy(selectedUser.id, e.target.value);
                      }
                    }}
                  >
                    <option value="">Select a study</option>
                    {studies.map(study => (
                      <option key={study.id} value={study.id}>
                        {study.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="sites">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium mb-4">Site Assignments</h3>
              <p className="text-gray-500">
                Assign users to specific sites. This is important for Investigators and Study Coordinators who need site-level access.
              </p>
              
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-3">Select User</h4>
                  <select 
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 appearance-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    value={selectedUser?.id || ""}
                    onChange={(e) => {
                      const userId = e.target.value;
                      const user = users.find(u => u.id === userId);
                      setSelectedUser(user);
                    }}
                  >
                    <option value="">Select a user</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-3">Assign to Site</h4>
                  <select 
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 appearance-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    onChange={(e) => {
                      if (selectedUser) {
                        handleAssignSite(selectedUser.id, e.target.value);
                      }
                    }}
                  >
                    <option value="">Select a site</option>
                    {sites.map(site => (
                      <option key={site.id} value={site.id}>
                        {site.site_code} - {site.site_name} ({site.study_name || 'No Study'})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <MapPin className="h-4 w-4 inline mr-1" />
                  <strong>Note:</strong> Investigators and Study Coordinators access the system at the site level, not the study level.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Add User Modal */}
      {canAddUsers && (
        <AddUserModal
          isOpen={isAddUserModalOpen}
          onClose={() => setIsAddUserModalOpen(false)}
          onSuccess={handleUserAdded}
          availableRoles={availableRoles}
          sponsorId={sponsorId} 
        />
      )}
    </div>
  );
};

export default UserManagement;
